import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import type { InventoryItem } from '../../../../shared/CharacterTypes';
import type { ItemData } from '../../components/ItemKit';
import { resolveMaxStack, slotsForQuantity } from '../../../../shared/inventory/bagCapacity';

// 'Setup' was removed 2026-10-07 (plan 06): no item type ever matched it.
export type InventoryTab = 'All' | 'Equip' | 'Use' | 'Etc';
export const INVENTORY_TABS: InventoryTab[] = ['All', 'Equip', 'Use', 'Etc'];
export const readableItemId = (id: string): string => id.replace(/[_-]+/g, ' ');
export const inventoryItemName = (item: InventoryItem): string => item.name || ITEM_DATABASE[item.id]?.name || 'Unknown item';
export const numericItemStats = (stats: InventoryItem['stats']): Record<string, number> | undefined => stats
  ? Object.fromEntries(Object.entries(stats).filter((entry): entry is [string, number] => typeof entry[1] === 'number')) : undefined;

export function inventoryItemData(item: InventoryItem): ItemData {
  const rarity = item.rarity === 'unique' ? 'legendary' : item.rarity;
  return { ...item, name: inventoryItemName(item), rarity, stats: numericItemStats(item.stats) };
}

export function matchesInventoryTab(item: InventoryItem, tab: InventoryTab): boolean {
  if (tab === 'All') return true;
  if (tab === 'Equip') return item.type === 'weapon' || item.type === 'armor';
  if (tab === 'Use') return item.type === 'consumable';
  return item.type === 'material' || item.type === 'currency';
}

/**
 * One row per bag SLOT (plan 06): an entry occupies `ceil(quantity / maxStack)`
 * slots (`src/shared/inventory/bagCapacity.ts`), so two copies of stack-limit-1
 * gear are two cells and 120 potions are a 99 and a 21. Keys are local
 * selection identity, never a fabricated item ID sent to the server.
 */
export function inventoryRows(items: InventoryItem[]): Array<{ item: InventoryItem; key: string }> {
  const copies = new Map<string, number>();
  return items.flatMap(item => {
    const stats = Object.entries(item.stats ?? {}).sort(([a], [b]) => a.localeCompare(b));
    const identity = JSON.stringify([item.id, item.name, item.type, item.rarity, stats]);
    const copy = copies.get(identity) ?? 0;
    copies.set(identity, copy + 1);
    const maxStack = resolveMaxStack(item.id, item);
    const slots = Math.max(1, slotsForQuantity(item.quantity, maxStack));
    return Array.from({ length: slots }, (_, slot) => ({
      item: slots === 1 ? item : { ...item, quantity: Math.min(maxStack, item.quantity - slot * maxStack) },
      key: `${identity}:${copy}:${slot}`,
    }));
  });
}

export function craftingItemName(id: string): string {
  return ITEM_DATABASE[id]?.name ?? `Unknown item · ${readableItemId(id)}`;
}
